import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ModernLogo } from "@/components/ModernLogo";
import { 
  Star, 
  Quote, 
  TrendingUp, 
  Clock, 
  DollarSign,
  Users,
  ArrowLeft,
  Award,
  CheckCircle
} from "lucide-react";
import { Link } from "react-router-dom";

export function SuccessStories() {
  const stories = [
    {
      name: "Mike Thompson",
      trade: "Electrician",
      location: "Manchester",
      yearsSince: "2 years",
      monthlyEarnings: "£3,500",
      story: "I switched from Checkatrade after paying £150/month for poor quality leads. With this platform, I pay nothing upfront and only 7% when I actually get work. I've increased my monthly earnings by 40% and work with genuine customers who value quality.",
      beforeAfter: {
        before: "£150/month subscription + poor leads",
        after: "No subscription + quality customers"
      },
      image: "/assets/tradesman-electrician.jpg",
      rating: 5,
      jobsCompleted: 127
    },
    {
      name: "Sarah Williams", 
      trade: "Plumber",
      location: "Birmingham",
      yearsSince: "18 months",
      monthlyEarnings: "£4,200",
      story: "As a female tradesperson, finding the right platform was crucial. Here, customers judge you on your work quality, not assumptions. The 2-hour response guarantee means I can build strong relationships with customers from the start.",
      beforeAfter: {
        before: "Struggling to find quality customers",
        after: "Consistent high-value jobs"
      },
      image: "/assets/tradesman-plumber.jpg",
      rating: 5,
      jobsCompleted: 89
    },
    {
      name: "David Chen",
      trade: "Carpenter",
      location: "London",
      yearsSince: "3 years",
      monthlyEarnings: "£5,800",
      story: "I was skeptical about the 7% commission model, but it's actually saved me thousands. On MyBuilder, I was paying hundreds monthly whether I got work or not. Now I only pay when I earn, and the customers are pre-verified with secured payments.",
      beforeAfter: {
        before: "£300+/month platform costs",
        after: "Pay only when working"
      },
      image: "/assets/tradesman-carpenter.jpg",
      rating: 5,
      jobsCompleted: 203
    },
    {
      name: "James Rodriguez",
      trade: "Heating Engineer", 
      location: "Leeds",
      yearsSince: "1 year",
      monthlyEarnings: "£4,500",
      story: "The emergency call-out feature has been a game-changer for my heating business. Customers can reach verified engineers within 2 hours, and I've built a loyal customer base through quick response times and quality work.",
      beforeAfter: {
        before: "Seasonal work fluctuations", 
        after: "Steady year-round income"
      },
      image: "/assets/tradesman-heating.jpg",
      rating: 5,
      jobsCompleted: 156
    },
    {
      name: "Emma Foster",
      trade: "Painter & Decorator",
      location: "Bristol", 
      yearsSince: "6 months",
      monthlyEarnings: "£2,800",
      story: "Just started my own business after years working for others. This platform gave me the confidence to go solo - no upfront costs, no contracts, just fair commission on completed work. The customer reviews have helped build my reputation quickly.",
      beforeAfter: {
        before: "Employee with limited income",
        after: "Successful business owner"
      },
      image: "/assets/tradesman-painter.jpg", 
      rating: 5,
      jobsCompleted: 43
    },
    {
      name: "Tom Wilson",
      trade: "Roofer",
      location: "Newcastle",
      yearsSince: "2.5 years", 
      monthlyEarnings: "£5,200",
      story: "Roofing work can be seasonal, so paying monthly platform fees was killing my winter earnings. Here, I work when I want and only pay commission on jobs I complete. The insurance verification gives customers confidence in my work.",
      beforeAfter: {
        before: "High fixed costs in slow months",
        after: "Flexible earnings model" 
      },
      image: "/assets/tradesman-roofer.jpg",
      rating: 5,
      jobsCompleted: 178
    }
  ];

  const stats = [
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      number: "2,500+",
      label: "Active Tradesmen",
      description: "Growing community"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary" />,
      number: "£850K+", 
      label: "Monthly Earnings",
      description: "Paid to tradesmen"
    },
    {
      icon: <Star className="w-8 h-8 text-primary" />,
      number: "4.8/5",
      label: "Average Rating",
      description: "Customer satisfaction"
    },
    {
      icon: <Clock className="w-8 h-8 text-primary" />,
      number: "98%",
      label: "Response Rate", 
      description: "Within 2 hours"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/for-tradesmen">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Back to For Tradesmen</span>
                  <span className="md:hidden sr-only">Back</span>
                </Button>
              </Link>
              <Link to="/">
                <Button variant="default" size="sm">
                  <span className="md:hidden">Start</span>
                  <span className="hidden md:inline">Get Started</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-hero/10 border border-primary/20 text-primary px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Award className="h-5 w-5" />
            Real Success Stories
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Tradesmen Transforming Their Businesses
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Discover how thousands of tradesmen have grown their earnings, reduced costs, 
            and built successful businesses with our platform.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-foreground mb-2">{stat.number}</div>
                <div className="font-semibold text-foreground mb-1">{stat.label}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real Stories from Real Tradesmen
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how our platform has helped tradesmen across the UK build thriving businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {stories.map((story, index) => (
              <Card key={index} className="border-border/50 shadow-elegant hover:shadow-glow transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center flex-shrink-0">
                      <Award className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">{story.name}</h3>
                      <p className="text-primary font-medium">{story.trade}</p>
                      <p className="text-sm text-muted-foreground">{story.location}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(story.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>

                  <div className="bg-primary/5 rounded-lg p-4 mb-6">
                    <Quote className="w-6 h-6 text-primary mb-2" />
                    <p className="text-muted-foreground italic leading-relaxed">
                      "{story.story}"
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-destructive/10 rounded-lg p-4">
                      <h4 className="font-semibold text-destructive mb-2">Before</h4>
                      <p className="text-sm text-muted-foreground">{story.beforeAfter.before}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4">
                      <h4 className="font-semibold text-green-600 mb-2">After</h4>
                      <p className="text-sm text-muted-foreground">{story.beforeAfter.after}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        <span>{story.monthlyEarnings}/month</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        <span>{story.jobsCompleted} jobs</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{story.yearsSince} with us</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-hero">
        <div className="container mx-auto text-center text-primary-foreground">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of successful tradesmen who have transformed their businesses with our platform.
          </p>
          <Link to="/">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
              Start Your Journey Today
            </Button>
          </Link>
          <p className="text-sm mt-4 opacity-75">
            No setup fees • No monthly contracts • Start earning immediately
          </p>
        </div>
      </section>
    </div>
  );
}